import React, { Component, useState, useEffect } from 'react'
import { connect, useDispatch } from 'react-redux'
import classnames from 'classnames'
import axios from 'axios'

import { getApiKey, createApiKey } from 'ducks/apikeys'
import { getMetrics } from 'ducks/metrics'
import { showModal, INTEGRATION_TRIAL_MODAL } from 'ducks/editor/modals'
import {
  showModal as showTrialModal,
  setPaymentRouteType,
} from 'ducks/trialWarning'
import Tooltip from 'components/Shared/Tooltip'

import { singularize } from 'utils/strings'
import { baseURL } from 'utils/io'
import { getIsOverLimit } from 'utils/metrics'

import { scroll } from 'ducks/editor/tables'

import Modal from 'components/Shared/Modal'
import Icon, { IconButton } from 'components/Shared/Icon'

import './Section.scss'
import { useFlags } from 'flags'
import { removeSpecialChars } from 'utils/regex'
import { getDatasource } from 'ducks/apps/datasources'
import { getTrialInformation, getUpgraded } from '../../../ducks/apps'

export const Header = ({
  name,
  appId,
  datasourceId,
  tableId,
  isUsersTable,
  forceShowPermissions, // This switch overrides the `tablePermissionsUI` experiment. Used by storybook. TODO(toby): Remove this switch when we remove the experiment.
  searchValue,
  onSearch,
  onCsvDownloadClick,
  onApiDocumentationClick,
  onPermissionsViewClick,
  metrics,
  datasource,
  paying,
}) => {
  const { tablePermissionsUi, collectionPermissionsPhase2 } = useFlags()

  const showPermissionsPhase1 =
    isUsersTable && (forceShowPermissions || tablePermissionsUi)

  let permissionsUrl = null

  if (collectionPermissionsPhase2) {
    permissionsUrl = `/apps/${appId}/data/${datasourceId}/permissions`
  } else if (showPermissionsPhase1) {
    permissionsUrl = `/apps/${appId}/data/${datasourceId}/${tableId}/permissions`
  }

  return (
    <Modal.Header
      title={name || 'Untitled'}
      color="orange"
      content={() => (
        <HeaderButtons
          name={name}
          appId={appId}
          datasourceId={datasourceId}
          tableId={tableId}
          searchValue={searchValue}
          onSearch={onSearch}
          onCsvDownloadClick={onCsvDownloadClick}
          onApiDocumentationClick={onApiDocumentationClick}
          displaySetPermissions={!!permissionsUrl}
          onPermissionsViewClick={() => onPermissionsViewClick(permissionsUrl)}
          metrics={metrics}
          datasource={datasource}
          paying={paying}
        />
      )}
    />
  )
}

const HeaderButtons = ({
  name,
  appId,
  datasourceId,
  tableId,
  searchValue,
  onSearch,
  onCsvDownloadClick,
  onApiDocumentationClick,
  displaySetPermissions,
  onPermissionsViewClick,
  metrics,
  datasource,
  paying,
}) => {
  const dispatch = useDispatch()
  const baseURL = `/apps/${appId}/data/${datasourceId}/${tableId}`
  const isOverLimit = getIsOverLimit(metrics, datasource)
  const disabled = isOverLimit && !paying

  const openModal = () => {
    dispatch(setPaymentRouteType('upgrade'))
    dispatch(showTrialModal())
  }

  return (
    <>
      <Modal.Button
        to={`${baseURL}/new`}
        outlined
        orange
        icon="plus"
        tooltip={disabled ? <UpgradeTooltip openModal={openModal} /> : null}
        disabled={disabled}
        placement="bottom"
      >
        Add {singularize(name)}
      </Modal.Button>
      {displaySetPermissions && (
        <IconButton
          type="security-badge"
          onClick={onPermissionsViewClick}
          tooltip="Set Permissions"
          orange
          placement="bottom"
        />
      )}
      {disabled ? (
        <IconButton
          tooltip={<UpgradeTooltip openModal={openModal} />}
          placement="bottom"
          orange
          disabled
          type="import-csv"
        />
      ) : (
        <IconButton
          tooltip="Import CSV"
          placement="bottom"
          to={`${baseURL}/csv-import`}
          orange
          type="import-csv"
        />
      )}
      <IconButton
        tooltip="Download CSV"
        placement="bottom"
        onClick={onCsvDownloadClick}
        orange
        type="export-csv"
      />
      <IconButton
        tooltip="API Documentation"
        placement="bottom"
        onClick={onApiDocumentationClick}
        orange
        type="code"
      />
      <Search
        tooltip="Search"
        placement="bottom"
        handleSearch={onSearch}
        searchValue={searchValue}
      />
    </>
  )
}

export const Actions = ({
  showDelete,
  deleteCount,
  onDeleteClick,
  onDoneClick,
}) => {
  let deletePrompt = 'Delete Records'

  if (deleteCount > 0) {
    deletePrompt = `Delete ${deleteCount.toString()} ${
      deleteCount === 1 ? 'Record' : 'Records'
    }`
  }

  return (
    <Modal.Actions
      leftButtons={
        <>
          {showDelete && (
            <Modal.Button type="button" outlined orange onClick={onDeleteClick}>
              {deletePrompt}
            </Modal.Button>
          )}
        </>
      }
    >
      <Modal.Button type="button" orange onClick={onDoneClick}>
        Done
      </Modal.Button>
    </Modal.Actions>
  )
}

class Section extends Component {
  state = {
    scrolled: false,
    scrollY: 0,
    modalSize: 0,
  }

  handleWheel = e => {
    e.preventDefault()
    const dx = e.deltaX
    const dy = e.deltaY
    const el = this.scrollEl

    el.scrollBy(dx, dy)
  }

  handleScroll = () => {
    if (!this.scrollEl) return null

    const { scroll } = this.props
    const { scrolled, scrollY } = this.state
    const scrollLeft = this.scrollEl.scrollLeft

    const newScrollY = this.scrollEl.scrollTop
    const newScrolled = scrollLeft >= 20

    let newState = this.state

    if (newScrolled !== scrolled) {
      newState = { ...newState, scrolled: newScrolled }
    }

    if (newScrollY !== scrollY) {
      newState = { ...newState, scrollY: newScrollY }
    }

    if (newState !== this.state) {
      this.setState(newState)
    }

    scroll(scrollLeft)
  }

  resetScroll = () => {
    const { scroll } = this.props

    scroll(0)
  }

  componentDidMount() {
    this.resetScroll()
  }

  contentWrapperRef = el => (this.scrollEl = el)

  wrapperRef = el => {
    if (this.wrapperEl) {
      this.wrapperEl.removeEventListener('wheel', this.handleWheel)
    }

    this.wrapperEl = el

    if (el) {
      this.wrapperEl.addEventListener('wheel', this.handleWheel, {
        passive: false,
      })
    }
  }

  handleCSVDownload = async () => {
    const { match } = this.props
    const { params } = match
    const { appId, datasourceId, tableId } = params

    const { data } = await axios.get(
      `${baseURL}/${appId}/data/${datasourceId}/${tableId}/export-csv`
    )

    let csv = `${data.fields.join(',')}\n`

    data.rows.forEach(row => {
      csv += row
        .map(string => (string === null ? '' : `\"${string}\"`))
        .join(',')

      csv += '\n'
    })

    const hiddenElement = document.createElement('a')

    hiddenElement.href = `data:text/csv;charset=utf-8, ${encodeURIComponent(
      csv
    )}`

    hiddenElement.target = '_blank'
    hiddenElement.download = `${data.name}.csv`
    hiddenElement.click()
  }

  handleAPIDocumentation = async () => {
    const { name, match, appApiKey, paying, createApiKey, showModal } =
      this.props

    const { appId } = match.params

    const sanitizedName = removeSpecialChars(name, true)

    const url = `/apps/${appId}/api-docs/#tag/${sanitizedName}`

    if (paying) {
      if (!appApiKey) {
        await createApiKey(appId)
      }

      window.open(url)
    } else {
      try {
        await showModal(INTEGRATION_TRIAL_MODAL, { type: 'api', appId })
        this.handleAPIDocumentation()
      } catch (err) {
        // modal closed
        console.error("modal closed, here's the error:", err)
      }
    }
  }

  handlePermissionsView = permissionUrl => {
    const { match, history } = this.props
    const { params } = match
    const { appId, datasourceId, tableId } = params

    return history.push(permissionUrl, {
      sourceTableId: tableId,
      returnUrl: `/apps/${appId}/data/${datasourceId}/${tableId}`,
    })
  }

  renderHeader = () => {
    const { handleSearch, searchValue, name, match } = this.props
    const { appId, datasourceId, tableId } = match.params

    return (
      <HeaderButtons
        searchValue={searchValue}
        name={name}
        appId={appId}
        datasourceId={datasourceId}
        tableId={tableId}
        onSearch={handleSearch}
        onCsvDownloadClick={this.handleCSVDownload}
        onApiDocumentationClick={this.handleAPIDocumentation}
      />
    )
  }

  getContentRect = () => {
    return this.scrollEl?.getBoundingClientRect?.()
  }

  render() {
    const {
      name,
      match,
      renderContent,
      handleCloseModal,
      blockedList,
      idList,
      handleSearch,
      searchValue,
      handleBlockedListToggle,
      handleSelectToggle,
      handleBulkDelete,
      blockedListLength,
      datasource,
      metrics,
      paying,
    } = this.props

    const { appId, datasourceId, tableId } = match.params
    const baseURL = `/apps/${appId}/data/${datasourceId}/${tableId}`

    const { scrollY } = this.state
    const deleteCount = blockedList ? blockedListLength : idList.length
    const showDelete = deleteCount > 0

    const isUsersTable = datasource.auth?.table === tableId

    return (
      <div onScroll={this.handleScroll} ref={this.wrapperRef}>
        <Header
          searchValue={searchValue}
          name={name}
          appId={appId}
          datasourceId={datasourceId}
          tableId={tableId}
          isUsersTable={isUsersTable}
          onSearch={handleSearch}
          onCsvDownloadClick={this.handleCSVDownload}
          onApiDocumentationClick={this.handleAPIDocumentation}
          onPermissionsViewClick={this.handlePermissionsView}
          metrics={metrics}
          datasource={datasource}
          paying={paying}
        />
        <Modal.Content childRef={this.contentWrapperRef} fluid>
          {renderContent(
            scrollY,
            this.getContentRect,
            blockedList,
            idList,
            handleBlockedListToggle,
            handleSelectToggle
          )}
        </Modal.Content>
        <Actions
          permissionsUrl={`${baseURL}/permissions`}
          isUsersTable={isUsersTable}
          showDelete={showDelete}
          deleteCount={deleteCount}
          onDeleteClick={() => handleBulkDelete(deleteCount)}
          onDoneClick={handleCloseModal}
        />
      </div>
    )
  }
}

const mapStateToProps = (state, { match }) => {
  const { appId, datasourceId } = match.params
  const { trialState } = getTrialInformation(state, appId)

  const paying = getUpgraded(state, appId) || trialState === 'during'

  return {
    appId,
    appApiKey: getApiKey(state, appId),
    datasource: getDatasource(state, appId, datasourceId),
    metrics: getMetrics(state, appId),
    paying,
  }
}

export default connect(mapStateToProps, {
  scroll,
  showModal,
  createApiKey,
})(Section)

const Search = props => {
  const [state, setState] = useState({ isOpen: false })
  const { isOpen } = state
  const { handleSearch, searchValue, tooltip, placement } = props
  const inputRef = React.createRef()

  const toggle = () => setState({ ...state, isOpen: !isOpen })

  const handleBlur = () => {
    if (searchValue.length === 0) return toggle()

    return null
  }

  useEffect(() => {
    if (isOpen) inputRef.current.focus()
  }, [isOpen])

  const renderContent = () => {
    return (
      <div className={classnames('data-modal-search', { expanded: isOpen })}>
        <IconButton orange type="search" onClick={toggle} />
        <input
          name="search"
          onChange={handleSearch}
          value={searchValue}
          placeholder="Search..."
          onBlur={handleBlur}
          ref={inputRef}
          autoComplete="off"
        />
      </div>
    )
  }

  return tooltip ? (
    <Tooltip placement={placement || 'top'} tooltip={tooltip}>
      {renderContent()}
    </Tooltip>
  ) : (
    renderContent()
  )
}

const UpgradeTooltip = ({ openModal }) => {
  return (
    <>
      <span>You reached the maximum record count.</span>
      <div className="tooltip-link-container" onClick={openModal}>
        <Icon type="open-in-new" small color="yellow" />
        <a onClick={openModal}>Upgrade your plan</a>
      </div>
    </>
  )
}
